<template>
  <div>
    <StaticDataTable class="static-table-list" v-if="generalPricingData">
      <template v-slot:header>
        <tr>
          <th>Code</th>
          <th width="20%">{{editing ? 'Alias' : 'Name'}}</th>
          <th>Price</th>
          <th>Actions</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr v-for="(stock, stockIndex) in generalPricingData.stockSupplierPriceList" :key="stock.id">
          <td class="font-weight-bold">
            <router-link :to="`/inventory/stock/${stock.id}`">{{stock.code}}</router-link>
            <span v-if="stock.isPendingUpdate" class="pricing-pending-update">
              &nbsp;<Tooltip label="Price updates pending"><i class="far fa-clock"></i></Tooltip>
            </span>
          </td>
          <td>
            <span v-if="!editing" :class="stock.alias ? 'font-italic' : ''">{{stock.alias ? stock.alias : stock.name}}</span>
            <FormInputText 
              v-if="editing"
              :value="stock.alias"
              :ref="`stock-alias-${stock.id}`"
              @input="setStockAlias(stock, $event)"
              placeholder="Stock Alias"
            />
          </td>
          <td>
            <template v-if="!editing || stock.isPendingUpdate">{{getPrice(stock)}}</template>
            <span v-show="editing && stock.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
            <FormInputCurrency 
                v-if="editing && !stock.isPendingUpdate"
                :value="getPriceValue(stock)"
                @input="setPriceValue(stock, $event)"
                @tab="onPriceTabbed($event, stockIndex)"
              />
          </td>
          <td>
            <a @click.prevent="removeEditingGlassPricing(stock.id)" v-if="editing && !stock.isPendingUpdate"><i class="far fa-trash-alt"></i></a>
            <ActionDropMenu :tableAction="true" :forceDropdown="true" v-if="!editing && stock.isPendingUpdate && userHasReadPermission(PermissionTypes.PricingPermissionTypes.pricingApproval)">
              <DropdownMenuItem :link="`/pricing/requests/${stock.stockPriceRequestGroupId}`" icon="fa-clock" label="Show Pending Prices">Show Pending Prices</DropdownMenuItem>
            </ActionDropMenu>
          </td>
        </tr>

        <tr v-if="editing && addingNewStock">
          <td :colspan="2">
            <div class="alert alert-warning" v-if="editingError">
              <i class="far fa-exclamation-triangle"></i>&nbsp;&nbsp;<strong>Error:</strong> {{editingError}}
            </div>
            <FormSearchableSelect ref="newStockInput" :options="lookupStockList" v-model="newStockId" @input="newStockSelected" placeholder="Stock Code" :loading="loading" v-if="addingNewStock" />
          </td>
          <td colspan="2">
            <ActionButton type="cancel" class="btn-xs ml-2" @click="cancelAddingStockItem()"> Cancel</ActionButton>
          </td>
        </tr>
        <tr v-if="editing && !addingNewStock">
          <td colspan="4">
            <ActionButton type="edit" class="btn-xs" @click="newStockButtonPressed" v-if="!addingNewStock"> New Stock</ActionButton>
          </td>
        </tr>
      </template>
    </StaticDataTable>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'
import StaticDataTable from '@/components/table/staticDataTable'
import { InventoryStockTypes, StockPriceTypes } from '@/store/types'

export default {
  name: 'SupplierGeneralPriceList',
  components: {
    StaticDataTable
  },
  props: {
    contactId: Number,
    supplierCurrencyCode: String,
    supplierGeneralPricingData: Object,
    editing: Boolean,
  },
  data() {
    return {
      loading: false,
      stockList: [],
      lookupStockList: [],
      addingNewStock: false,
      newStockId: null,
      generalPricingData: _.cloneDeep(this.$props.supplierGeneralPricingData),
      editingError: null
    }
  },
  watch: {
    supplierGeneralPricingData: function() {
      this.$data.generalPricingData = _.cloneDeep(this.$props.supplierGeneralPricingData)
    }
  },
  computed: {
    ...mapState({
      accountCurrencyCode: state => state.account.currencyCode,
    })
  },
  mounted() {
    this.loadStockList()
  },
  methods: {
    getPriceValue(stock) {
      if(stock.prices == null)
        return undefined
      var priceInfo = stock.prices[0]
      if(priceInfo == null)
        return undefined
      return priceInfo.price
    },
    setPriceValue(stock, value) {
      stock.prices[0].price = value
      this.$emit('update', this.$data.generalPricingData)
    },
    getPrice(stock) {
      var value = this.getPriceValue(stock)
      if(value === undefined)
        return '-'
      return this.$options.filters.formatCurrency(value) + ' ' + (this.supplierCurrencyCode || '')
    },
    setStockAlias(stock, alias) {
      stock.alias = alias
      this.$emit('update', this.$data.generalPricingData)
    },
    loadStockList() {
      this.loading = true
      this.$apiService.inventoryService.getStockList().then(response => {
        this.$data.stockList = response.data

        this.$data.lookupStockList = this.$data.stockList
          .filter(stock => stock.isActive && stock.type == InventoryStockTypes.general)
          .map(stock => {
            return {
              value: stock.id,
              name: `${stock.code} | ${stock.name}`
            }
          })
      })
      .finally(() => {
        this.loading = false
      })
    },
    newStockButtonPressed() {
      this.addingNewStock = true
      this.editingError = ''
      this.$nextTick(() => {
        this.$refs.newStockInput.focus()
        this.$refs.newStockInput.select()
      })
    },
    cancelAddingStockItem() {
      this.addingNewStock = false
      this.newStockId = null
    },
    newStockSelected(stockId) {
      if(this.addNewStockItem(stockId)) {
        this.$nextTick(() => {
          let aliasRef = `stock-alias-${stockId}`
          this.$refs[aliasRef][0].focus()
          this.$refs[aliasRef][0].select()
        })
      }
    },
    addNewStockItem(stockId) {
      this.editingError = null
      if(stockId == null)
        return false

      // Check if the code is already in the list
      if(this.generalPricingData.stockSupplierPriceList.find(stock => stock.id == stockId) != null) {
        this.editingError = 'Stock item is already in the list'
        return false
      }

      // Get stock info from list
      var stockItem = this.$data.stockList.find(stock => stock.id == stockId)
      if(stockItem == null) {
        this.editingError = 'Could not find stock item'
        return false
      }

      // Add an item to the list
      this.generalPricingData.stockSupplierPriceList.push({
        id: stockId,
        code: stockItem.code,
        name: stockItem.name,
        prices: [{
          price: null
        }]
      })

      this.$emit('update', this.$data.generalPricingData)

      this.addingNewStock = false
      this.newStockId = null

      return true
    },
    removeEditingGlassPricing(stockId) {
      this.generalPricingData.stockSupplierPriceList = this.generalPricingData.stockSupplierPriceList.filter(stock => stock.id != stockId)
      this.$emit('update', this.$data.generalPricingData)
    },
    onPriceTabbed(evt, stockIndex) {
      // If the last stock item then start a new stock row
      if(stockIndex >= this.generalPricingData.stockSupplierPriceList.length-1) {
        evt.preventDefault()
        this.newStockButtonPressed()
      }
    }
  }  
}
</script>

